/**
 * MONSTER TOWN — TOWN LIFE. NPC CREW (src/npc/**). The retention engine.
 *
 * Neighbours move in as the town grows, potter about where the kid can watch,
 * walk over to things the kid built, say hello when the kid is near, queue up at
 * the venue when the venue asks, and leave presents on the doorstep while the
 * kid is away.
 *
 * THREE PROMISES THIS CREW KEEPS
 *  1. LAW #2 — nothing here can be failed, rushed or missed. A neighbour who is
 *     ignored just carries on being pleasant. A present waits forever.
 *  2. The older iPad — a hard population cap, 'simple' detail art, per-frame
 *     work only for neighbours in the scene you are actually looking at.
 *  3. Saves are sacred — the roster is its own versioned section with defensive
 *     hydration; a corrupt row loses one neighbour, never the town.
 *
 * WHAT OTHER CREWS USE: see `src/npc/events.ts`. Nobody imports this file.
 *   who lives here + presents -> registry.ts
 *   the roster on disk        -> persistence.ts
 *   what a neighbour decides  -> brain.ts
 *   harness angles and demos  -> demos.ts
 */

import { bus } from '../core/events'
import { engine } from '../core/engine'
import { save } from '../core/save'
import { world } from '../core/scene'
import type { NpcId } from '../core/types'
import { app, type GameContext } from '../app/context'
import { playerPosition } from '../monster/player'
import type { Npc } from './npc'
import { think, roamPoint, type BrainContext } from './brain'
import { ARCHETYPES, archetypeLook } from './roster'
import { registerNpcHarness } from './demos'
import { lastSeen, markSeenNow, registerNpcSave } from './persistence'
import {
  GIFT_DEF,
  MAX_NPCS,
  beginGiftOpen,
  dropGift,
  ensurePopulation,
  npcs,
  scaleFor,
  separate,
  spawnNeighbour,
  tickGifts,
  townNode,
} from './registry'
import './events'

/** How often the population catches up after a load or a build spree. */
const REPOPULATE_EVERY = 6
/** Below this, "away" was just a page reload, not a night's sleep. */
const AWAY_MIN_MS = 20 * 60 * 1000
const AWAY_LONG_MS = 6 * 60 * 60 * 1000

let installed = false
let repopIn = 2

// ---------------------------------------------------------------------------
// while you were away
// ---------------------------------------------------------------------------

/**
 * The "what happened while you were gone" beat. Only fires after a real absence
 * so a page reload never spends the surprise.
 */
function runAwayReport(): void {
  const node = townNode()
  if (node === null) return
  const previous = lastSeen()
  const now = markSeenNow()
  const away = previous > 0 ? Math.max(0, now - previous) : 0

  if (away < AWAY_MIN_MS) {
    ensurePopulation(false)
    return
  }

  const player = playerPosition()
  let gifts = 0
  let newNeighbours = 0

  const giftCount = away >= AWAY_LONG_MS ? 2 : 1
  for (let i = 0; i < giftCount; i++) {
    const id = dropGift(node, player)
    if (id === null) continue
    gifts++
    const giver = [...npcs.values()][0]
    bus.emit('npc:leftGift', {
      npcId: giver?.id ?? ('n0' as NpcId),
      sceneId: node.def.id,
      at: node.grid.cellToWorld(node.grid.placement(id)?.anchor ?? { gx: 0, gy: 0, gz: 0 }),
      instanceId: id,
    })
  }

  if (away >= AWAY_LONG_MS && npcs.size < MAX_NPCS) {
    const npc = spawnNeighbour(node, roamPoint(node.grid, player, engine.rng, 5), true)
    if (npc !== null) newNeighbours++
  }

  ensurePopulation(false)

  bus.emit('townlife:awayReport', {
    awayMs: away,
    gifts,
    newNeighbours,
    visitors: npcs.size,
  })
  save.request('townlife:away')
}

// ---------------------------------------------------------------------------
// install
// ---------------------------------------------------------------------------

/**
 * WHOSE FACE IS ON THE ERRAND CARD.
 *
 * `src/errands/giver.ts` draws a giver from a hash of its id until somebody who
 * actually owns neighbours says otherwise — see the note on `errand:giverLook`
 * in `src/errands/events.ts`, which asks for exactly this and nothing else. We
 * own the roster, so we answer it.
 *
 * Without this the card that arrives when Bramble walks over and waves wears a
 * DIFFERENT monster from the one standing in front of the kid. To a
 * three-year-old, who cannot read either of their names, that is not a small
 * inconsistency — it is two monsters.
 *
 * DERIVED FROM THE ROSTER, so it is a data drop both ways: a new archetype gets
 * a face on its cards for free, and an errand written against `npc_zookeeper`
 * finds whoever currently keeps the zoo. Two id shapes, because the errand data
 * names givers both ways:
 *   `npc_neighbour_<name>`  — a particular monster ("Bramble wants a bench")
 *   `npc_<role>`            — whoever does that job ("the zookeeper")
 * An id nothing here claims is untouched and keeps its derived face, so this can
 * only ever make a card MORE right.
 */
function publishGiverFaces(): void {
  for (const a of ARCHETYPES) {
    const look = archetypeLook(a)
    bus.emit('errand:giverLook', { giver: `npc_neighbour_${a.name.toLowerCase()}`, look })
    // `role_zookeeper` -> `npc_zookeeper`. First archetype with the role wins,
    // so the town has one zookeeper's face however many keep that rhythm.
    const role = a.routine.startsWith('role_') ? a.routine.slice('role_'.length) : ''
    if (role !== '' && !rolesPublished.has(role)) {
      rolesPublished.add(role)
      bus.emit('errand:giverLook', { giver: `npc_${role}`, look })
    }
  }
}
const rolesPublished = new Set<string>()

export function installNpcs(_ctx: GameContext): void {
  if (installed) return
  installed = true

  registerNpcSave()
  /**
   * ON `app:booted`, not here: this crew installs BEFORE the errand crew (see
   * the order in `src/main.ts`), so a face emitted at install time would be
   * shouted into an empty room. `app:booted` is the engine's first frame, by
   * which point every crew has subscribed.
   */
  bus.on('app:booted', publishGiverFaces)

  // -- the town keeps growing as the kid builds --------------------------
  bus.on('build:placed', () => ensurePopulation(true))

  // -- neighbours follow the kid between scenes when asked ---------------
  bus.on('scene:entered', (e) => {
    const node = world.get(e.scene)
    if (node === undefined) return
    for (const npc of npcs.values()) {
      if (npc.sceneId !== e.scene) continue
      npc.attach(node)
      npc.setScale(scaleFor(node))
    }
  })

  // The creator is about the kid's own monster. Neighbours step off-stage so
  // nothing else is moving while a 3-year-old is picking eyes.
  bus.on('app:phaseChanged', (e) => {
    const hide = e.phase === 'monsterCreator'
    for (const npc of npcs.values()) npc.setVisible(!hide)
  })

  // -- venue / any-crew control -----------------------------------------
  bus.on('npc:requestCustomer', (e) => {
    const node = world.get(e.sceneId)
    if (node === undefined) return
    let chosen: Npc | null = null
    for (const npc of npcs.values()) {
      // A neighbour who has gone in for the night is not standing about waiting
      // to be a customer — pulling one out would teleport a monster out of a
      // house it is asleep in. If the whole town is asleep, one more moves in.
      if (!npc.scripted && !npc.indoors) {
        chosen = npc
        break
      }
    }
    if (chosen === null) chosen = spawnNeighbour(node, e.at, false)
    if (chosen === null) return
    chosen.attach(node)
    chosen.setIndoors(false)
    chosen.scripted = true
    chosen.activity = 'customer'
    chosen.focus = e.tag ?? null
    chosen.mood = 'hungry'
    chosen.walkTo(e.at, 'lookAround', 0.4)
  })

  /**
   * THE TUTORIAL'S GUIDE. Whoever is free walks over and points; if the whole
   * roster is busy a new neighbour moves in to do it, because a first run with
   * nobody to show them round is the one case this must not fail.
   *
   * They are marked `scripted` so town life stops steering them, and the
   * tutorial hands them back with `npc:dismiss` when it is done.
   */
  bus.on('npc:requestGuide', (e) => {
    const node = world.get(e.sceneId)
    if (node === undefined) return
    let chosen: Npc | null = null
    for (const npc of npcs.values()) {
      if (npc.scripted || npc.indoors || npc.sceneId !== e.sceneId) continue
      chosen = npc
      break
    }
    if (chosen === null) chosen = spawnNeighbour(node, e.at, false)
    if (chosen === null) return
    chosen.attach(node)
    chosen.setIndoors(false)
    chosen.scripted = true
    chosen.activity = 'greet'
    chosen.mood = 'happy'
    chosen.walkTo(e.at, 'point', 0.4)
    bus.emit('npc:guideReady', { npcId: chosen.id, sceneId: e.sceneId })
  })

  bus.on('npc:sendTo', (e) => {
    const npc = npcs.get(e.npcId)
    if (npc === undefined) return
    const node = world.get(e.sceneId)
    if (node !== undefined) npc.attach(node)
    npc.scripted = true
    npc.walkTo(e.target, e.then ?? null, 0.4)
  })

  bus.on('npc:emote', (e) => {
    npcs.get(e.npcId)?.act(e.action, e.seconds)
  })

  bus.on('npc:dismiss', (e) => {
    const npc = npcs.get(e.npcId)
    if (npc === undefined) return
    save.request('npc:dismiss')
    npc.scripted = false
    npc.activity = 'wander'
    npc.focus = null
    npc.mood = e.happy === false ? 'curious' : 'delighted'
    npc.act(e.happy === false ? 'nod' : 'cheer')
    npc.timer = 2
  })

  // Meeting the kid is worth remembering: "a NEW monster arrived" only reads
  // as news if the game knows which neighbours are already friends.
  bus.on('npc:reacted', (e) => {
    const npc = npcs.get(e.npcId)
    if (npc === undefined || npc.known) return
    npc.known = true
    save.request('npc:met')
  })

  // -- our own prop: open the present the kid just tapped ----------------
  bus.on('play:interacted', (e) => {
    if (e.defId !== GIFT_DEF) return
    beginGiftOpen(e.instanceId, e.sceneId)
  })

  // -- daily return, once the save has landed ----------------------------
  bus.on('save:loaded', () => {
    setTimeout(() => runAwayReport(), 0)
  })

  // -- the loop ----------------------------------------------------------
  engine.addSystem({
    id: 'npc.townLife',
    order: 40,
    fixed: (dt) => {
      repopIn -= dt
      if (repopIn <= 0) {
        repopIn = REPOPULATE_EVERY
        ensurePopulation(false)
      }
      const activeId = world.activeSceneId
      const node = activeId === null ? undefined : world.get(activeId)
      if (node !== undefined) {
        const brain: BrainContext = {
          node,
          grid: node.grid,
          playerPos: playerPosition(),
          rng: engine.rng,
          homeSpot: app.homeSceneId === node.def.id ? null : playerPosition(),
        }
        for (const npc of npcs.values()) {
          if (npc.sceneId !== activeId) continue
          npc.step(dt, node.grid)
          think(npc, brain)
        }
        separate()
      }
      tickGifts(dt)
    },
    render: (_alpha, frameMs) => {
      const activeId = world.activeSceneId
      const d = Math.min(0.1, frameMs / 1000)
      for (const npc of npcs.values()) {
        if (npc.sceneId !== activeId) continue
        npc.render(d)
      }
    },
  })

  registerNpcHarness()
  // `installHarness()` runs after every crew and re-registers its placeholder
  // angles, so claim the real `monster-creator` framing once boot has finished.
  setTimeout(() => registerNpcHarness(), 0)
}

export { npcRoster } from './registry'

/** How many archetypes exist — used by the roster demo and tests. */
export const ARCHETYPE_COUNT = ARCHETYPES.length
